/**
 * ═══════════════════════════════════════════════════════════════════════════
 * DER LISTENAUSZUG IST KEIN PROJEKT (Vorfall 21.10.2026)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ─── Der Schaden ───────────────────────────────────────────────────────────
 * Die Projektliste (`doLoadProjectsFromCloud`) liest aus dem Blob nur den
 * Zweig `data->metadata` (`PROJECT_COLUMNS_LIST` in queryColumns.ts) und baut
 * daraus `p.data = { metadata }`. „Verschieben" und „Überspringen" (Dashboard,
 * Projektliste, Kanban) haben genau diesen Auszug als GANZES Projekt an
 * `saveProjectToCloud` gegeben. Der Speicherweg schreibt `projects.data`
 * vollständig — jeder Schlüssel außerhalb von `metadata` war danach weg:
 * `settings`, `unterlage` (Projektdaten der Unterlage), `vorankuendigung`,
 * `mutualHazards`, `safetyPoints`, `unterlageVersions`. In ProjectOverview
 * sogar zweimal hintereinander (eigener Save, dann der App-Handler).
 *
 * Die Kindtabellen kamen glimpflicher davon, aber nicht unberührt: die
 * Leistungspositionen gingen mit den Zählern aus dem Listenstand zurück (ohne
 * Anker unbedingt), die Behalte-Liste war die der Liste. Mängel und
 * Checklisten fasste der Weg nicht an (der Auszug hat keine), Notizen, Pläne,
 * Unterlage-Einträge und Anhangbilder kamen leer an — dort hält nur der
 * Leer-Riegel (`pruneOrphans` / save_project_children_tx: leere Behalte-Liste
 * löscht nichts) das Löschen auf.
 *
 * ─── Der Riegel ────────────────────────────────────────────────────────────
 * Jeder Listenladeweg kennzeichnet seinen Auszug (`markiereListenAuszug`), und
 * `saveProjectToCloud` weist einen gekennzeichneten Stand ab, BEVOR irgendetwas
 * die Datenbank erreicht (`verweigereListenAuszug`). Die Kennung sitzt AM
 * `data`-Objekt und ist aufzählbar: jeder Spread des Auszugs
 * (`{ ...project.data, metadata: … }`) trägt sie mit — genau die Form, in der
 * der Schaden entstand. Ein voller Ladeweg (`loadProjectById`) liefert sie
 * nie (`ohneListenAuszugKennung`); sie gelangt auch nicht in die Datenbank,
 * weil der einzige Schreibweg sie abweist.
 *
 * Wer aus der Liste heraus ein Projekt ändern will: `loadProjectById` →
 * ändern → `saveProjectToCloud` (Muster: folgeterminService.ts).
 *
 * Rein und ohne Supabase-Abhängigkeit.
 */

/** Der Schlüssel am `data`-Objekt eines Listeneintrags. */
export const LISTEN_AUSZUG_KENNUNG = '__listenAuszug' as const;

/** Klartext für Log und Fehlermeldung. */
export const LISTEN_AUSZUG_TEXT =
    'Speichern abgewiesen: Das ist ein Eintrag der Projektliste, kein vollständig geladenes Projekt. '
    + 'Gespeichert hätte er alle Projektinhalte außerhalb der Metadaten gelöscht '
    + '(Unterlage, Vorankündigung, Einstellungen, Gefährdungen …). Es wurde NICHTS geschrieben.';

/**
 * Kennzeichnet `data` als Listenauszug — am Objekt selbst, aufzählbar, damit
 * ein Spread die Kennung mitnimmt. Gibt dasselbe Objekt zurück.
 */
export const markiereListenAuszug = <T>(data: T): T => {
    if (data && typeof data === 'object') {
        (data as Record<string, unknown>)[LISTEN_AUSZUG_KENNUNG] = true;
    }
    return data;
};

/** Trägt dieser Stand die Kennung eines Listenauszugs? */
export const istListenAuszug = (report: unknown): boolean =>
    !!report
    && typeof report === 'object'
    && (report as Record<string, unknown>)[LISTEN_AUSZUG_KENNUNG] === true;

/**
 * Entfernt die Kennung — für VOLLE Ladewege. Normal steht sie nie in der
 * Datenbank (der Schreibweg weist sie ab); stünde sie doch einmal dort, wäre
 * das Projekt sonst dauerhaft unspeicherbar.
 */
export const ohneListenAuszugKennung = <T>(data: T): T => {
    if (data && typeof data === 'object' && LISTEN_AUSZUG_KENNUNG in (data as object)) {
        delete (data as Record<string, unknown>)[LISTEN_AUSZUG_KENNUNG];
    }
    return data;
};

/**
 * Ein Speichern, das einen Listenauszug als Projekt schreiben wollte.
 *
 * `status = 422`: die Warteschlange stuft das als 'blocked' ein
 * (classifySyncError) — der Eintrag bleibt lokal liegen statt endlos
 * wiederholt zu werden. Hineingeraten darf er dort ohnehin nicht.
 */
export class ListenAuszugSpeichernError extends Error {
    readonly name = 'ListenAuszugSpeichernError';
    readonly code = 'listen-auszug';
    readonly status = 422;

    constructor(readonly projectId: string | null) {
        super(LISTEN_AUSZUG_TEXT);
    }
}

/**
 * Der Riegel am Anfang von `saveProjectToCloud`: wirft, wenn `report` ein
 * Listenauszug ist. Laut, nicht still — ein abgewiesener Auszug ist immer ein
 * Programmfehler im Aufrufer, und der soll im Log stehen.
 */
export const verweigereListenAuszug = (report: unknown, projectId?: string | null): void => {
    if (!istListenAuszug(report)) return;
    const fehler = new ListenAuszugSpeichernError(projectId ?? null);
    console.error('[listenAuszug] Speichern eines Listeneintrags abgewiesen', {
        projectId: projectId ?? null,
        schluessel: Object.keys(report as object),
    }, fehler);
    throw fehler;
};
